import React, { useState, useEffect, useContext } from "react";
import {
    PDFDownloadLink,
    Document,
    Page,
    Text,
    View,
    Image,
    StyleSheet,
    Font,
} from "@react-pdf/renderer";
import CenturyGothicFont from "Assets/fonts/centurygothic.ttf";
import CenturyGothicFontBold from "Assets/fonts/centurygothic_bold.ttf";
import { renderHtmlToReactPdf } from "./utils/RenderHtmlToReactPdf"
import { minimizeHtmlSpacing, removePreImageContent } from "./utils/MinimizeHtmlSpacing";
import { JobDataContext } from "State/JobsDataContext";



Font.register({
    family: 'CenturyGothic',
    fonts: [
        { src: CenturyGothicFont, fontStyle: 'italic' },
        { src: CenturyGothicFontBold, fontStyle: 'bold' },
        { src: CenturyGothicFont },
    ]
});

function extractFirstImageUrl(htmlString) {
    const match = htmlString.match(/<img\s+(?:[^>]*?\s+)?src\s*=\s*(["'])(.*?)\1/i);
    return match ? match[2] : null;
}

async function getBase64Image(url) {
    if (!url) return null;
    try {
        const response = await fetch(url);
        const blob = await response.blob();
        return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(blob);
        });
    } catch (error) {
        return null;
    }
}

const SbDownloadPdf = ({ content }) => {
    const { selectedJob } = useContext(JobDataContext);
    const jobData = selectedJob;

    const fileName = `${jobData?.sb_requisition_number || "Position#"}_${jobData?.job_title || "CompanySpecificRoleTitle"}_V${jobData?.version || "Version#"}.pdf`;


    const [base64Image, setBase64Image] = useState(null);
    const [loading, setLoading] = useState(true);
    console.log({ content });
    let cachedImageSrc = null;
    const firstImageUrl = content ? extractFirstImageUrl(content) : null;

    useEffect(() => {
        if (!firstImageUrl) {
            setLoading(false);
            cachedImageSrc = null;
            return;
        }

        const fetchImage = async () => {
            setLoading(true);
            const base64 = await getBase64Image(firstImageUrl);

            cachedImageSrc = base64;
            setBase64Image(base64);
            setLoading(false);
        };
        fetchImage();
    }, [firstImageUrl, content]);

    const styles = StyleSheet.create({
        page: {
            flexDirection: 'column',
            padding: 30,
            fontFamily: 'CenturyGothic',
            fontSize: 10,
            lineHeight: 1.4,
            position: 'relative',
        },
        content: {},
        text: {
            fontFamily: 'CenturyGothic',
            fontSize: 10,
            lineHeight: 1.4
        },
        watermark: {
            position: 'absolute',
            top: '40%',
            left: 0,
            right: 0,
            textAlign: 'center',
            fontSize: 100,
            color: 'grey',
            opacity: 0.2,
            transform: 'rotate(-45deg)',
            zIndex: -1,
        },
        section: {
            marginTop: 10,
            marginBottom: 0,
        },
        disclaimerHeading: {
            fontSize: 13,
            fontWeight: 'bold',
            fontFamily: 'CenturyGothic',
            marginBottom: 4,
        },
        disclaimer: {
            marginTop: 3,
            marginBottom: 1,
            fontFamily: 'CenturyGothic',
            color: '#2B3034',
            fontSize: 11,
            lineHeight: 1.2,
        },
    });

    const minimizedHtmlContent = minimizeHtmlSpacing(content);
    const cleanedHtmlContent = removePreImageContent(minimizedHtmlContent, firstImageUrl);
    const parsedContent = new DOMParser().parseFromString(`<html><body>${cleanedHtmlContent}</body></html>`, 'text/html');
    const parsedReactComponents = Array.from(parsedContent.body.children).map(
        (node, index) => <React.Fragment key={index}>{renderHtmlToReactPdf(node, styles.text)}</React.Fragment>
    ).filter(Boolean);


    const renderDisclaimer = () => (
        <View style={styles.section}>
            <Text style={styles.disclaimerHeading}>Disclaimer:</Text>
            <Text style={styles.disclaimer}>
                This job description is provided for illustrative and demonstration purposes only. It was generated using artificial intelligence technology. As an AI-generated document, it may not fully reflect current industry standards, specific organizational needs, or human expertise.
            </Text>
            <Text style={styles.disclaimer}>
                Users are encouraged to independently verify the accuracy and applicability of this job description to their specific situation before relying on it for any decisions.
            </Text>
            <Text style={styles.disclaimer}>
                SkillsBridge.AI disclaims all liability for any errors, omissions, inaccuracies, or potential biases in this document. Any reliance you place on such information is strictly at your own risk. Unauthorized use, disclosure, or distribution is strictly prohibited.
            </Text>
        </View>
    );

    const SbJdDocument = () => (
        <Document>
            <Page style={styles.page}>
                <Text style={styles.watermark} fixed>DRAFT</Text>
                <View style={styles.content}>
                    {base64Image && (
                        <Image
                            src={base64Image}
                            style={{ marginVertical: 4, maxWidth: "100%", height: 'auto' }}
                        />
                    )}
                    {parsedReactComponents}
                    {renderDisclaimer()}
                </View>
            </Page>
        </Document>
    );

    if (loading) {
        return (
            <svg
                cursor={"not-allowed"}
                width="30"
                height="30"
                viewBox="0 0 30 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <rect width="30" height="30" rx="3" fill="#E0E0E0" />
            </svg>
        );
    }

    return (
        <PDFDownloadLink
            document={<SbJdDocument />}
            fileName={fileName}
        >
            {({ loading: pdfLoading }) => (
                <svg
                    cursor={pdfLoading ? "not-allowed" : "pointer"}
                    width="30"
                    height="30"
                    viewBox="0 0 30 30"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <rect width="30" height="30" rx="3" fill="white" />
                    <path
                        d="M6.5 15.8889V21.2222C6.5 21.6937 6.72388 22.1459 7.1224 22.4793C7.52091 22.8127 8.06141 23 8.625 23H21.375C21.9386 23 22.4791 22.8127 22.8776 22.4793C23.2761 22.1459 23.5 21.6937 23.5 21.2222V15.8889M15 7V17.6667M15 17.6667L11.2812 14.5556M15 17.6667L18.7188 14.5556"
                        stroke="#8790A4"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </svg>
            )}
        </PDFDownloadLink>
    );
};

export default SbDownloadPdf;